import {Button} from '@wandb/components';
import {parseRunTabPath} from '@wandb/weave/common/util/url';
import React, {useMemo} from 'react';

import {FileBrowserLayout} from './FileBrowserLayout';
import {FileView} from './FileView/FileView';
import {FolderView} from './FolderView/FolderView';
import {makeFileTree, type Node} from './makeFileTree';
import type {
  FileBrowserFile,
  RunFileScope,
  UseFileBrowserLoadFile,
  UseFileBrowserLoadFileUrl,
} from './types';

interface FileBrowserProps {
  artifactId: string | null;
  totalFiles: number;
  files: FileBrowserFile[];
  path?: string;
  useLoadFile: UseFileBrowserLoadFile;
  useLoadFileUrl: UseFileBrowserLoadFileUrl;
  setFilePath(path: string[]): void;
  onDownload(file: FileBrowserFile): void;
  runFileScope?: RunFileScope;
}

export default function FileBrowser({
  artifactId,
  files,
  onDownload,
  runFileScope,
  path,
  setFilePath,
  totalFiles,
  useLoadFile,
  useLoadFileUrl,
}: FileBrowserProps) {
  const filePath = parseRunTabPath(path);
  const pathString = filePath.join('/');
  // oxlint-disable-next-line react/preserve-manual-memoization
  let currentFolder: Node | undefined = useMemo(
    () => makeFileTree(files),
    [files]
  );
  // traverse the tree to the current directory
  let currentFile: FileBrowserFile | undefined;
  filePath.forEach(folderName => {
    if (currentFolder == null) {
      return;
    }
    if (currentFolder.subdirectories[folderName] != null) {
      currentFolder = currentFolder.subdirectories[folderName];
    } else {
      currentFile = currentFolder.files.find(f => f.name === pathString);
      currentFolder = undefined;
      if (currentFile == null) {
        return;
      }
    }
  });
  if (currentFile) {
    return (
      <FileView
        artifactId={artifactId}
        file={currentFile}
        filePath={filePath}
        runFileScope={runFileScope}
        onDownload={onDownload}
        useLoadFile={useLoadFile}
        useLoadFileUrl={useLoadFileUrl}
      />
    );
  }
  if (currentFolder) {
    return (
      <FolderView
        filePath={filePath}
        folder={currentFolder}
        runFileScope={runFileScope}
        onDownload={onDownload}
        onSetFilePath={setFilePath}
        totalFiles={totalFiles}
        useLoadFile={useLoadFile}
      />
    );
  }

  return (
    <FileBrowserLayout
      topLeft={
        <h3 className="text-[20px] leading-[20px] font-bold">File not found</h3>
      }>
      <p>
        Couldn't find file or folder at path: <code>{pathString}</code>
      </p>
      <Button startIcon="home" onPress={() => setFilePath([])}>
        Go to file root
      </Button>
    </FileBrowserLayout>
  );
}
